import React from 'react';
import { motion } from 'framer-motion';
import { Quote, Star } from 'lucide-react';

const testimonials = [
  {
    quote: "Vrittaa AI transformed how we handle customer support. Our response time dropped by 80% while maintaining quality.",
    name: "David Chen",
    role: "CTO, FinTech Solutions",
    image: "https://images.unsplash.com/photo-1576558656222-ba66febe3dec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc3MjE4MjAwNHww&ixlib=rb-4.1.0&q=85&w=200",
    rating: 5,
  },
  {
    quote: "The agentic workflows are a game-changer. Our agents now handle complex tasks that used to require human oversight.",
    name: "Sarah Miller",
    role: "Lead Automation Engineer",
    image: "https://images.pexels.com/photos/30004324/pexels-photo-30004324.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    rating: 5,
  },
  {
    quote: "Finally, an automation platform that thinks like we do. Integration was seamless, and the ROI was immediate.",
    name: "James Wilson",
    role: "Product Director",
    image: "https://images.unsplash.com/photo-1762522926157-bcc04bf0b10a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc3MjE4MjAwNHww&ixlib=rb-4.1.0&q=85&w=200",
    rating: 5,
  },
];

const companies = [
  { name: 'TechCorp', initial: 'T' },
  { name: 'InnovateLabs', initial: 'I' },
  { name: 'DataDriven', initial: 'D' },
  { name: 'ScaleUp', initial: 'S' },
  { name: 'FutureStack', initial: 'F' },
];

const TestimonialCard = ({ quote, name, role, image, rating, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.15, duration: 0.5 }}
      viewport={{ once: true }}
      className="group p-6 md:p-8 rounded-2xl bg-slate-900/30 border border-emerald-500/10 hover:border-emerald-500/20 transition-all duration-300"
      data-testid={`testimonial-card-${index}`}
    >
      {/* Quote icon */}
      <div className="mb-4">
        <Quote className="w-8 h-8 text-emerald-500/30" />
      </div>

      {/* Rating */}
      <div className="flex gap-1 mb-4">
        {[...Array(rating)].map((_, i) => (
          <Star key={i} className="w-4 h-4 fill-yellow-500 text-yellow-500" />
        ))}
      </div>

      {/* Quote text */}
      <p className="text-slate-300 text-sm md:text-base leading-relaxed mb-6">
        "{quote}"
      </p>

      {/* Author */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-slate-800">
          <img
            src={image}
            alt={name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
        <div>
          <div className="font-semibold text-white text-sm">{name}</div>
          <div className="text-xs text-slate-500">{role}</div>
        </div>
      </div>
    </motion.div>
  );
};

const Testimonials = () => {
  return (
    <section
      data-testid="testimonials-section"
      className="relative py-24 lg:py-32 px-6"
    >
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-indigo-500/5 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="uppercase tracking-[0.2em] text-xs font-semibold text-emerald-400 mb-4 block">
            Trusted by Leaders
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-outfit text-white mb-4">
            Hear From Our <span className="text-gradient">Customers</span>
          </h2>
          <p className="text-slate-400 max-w-xl mx-auto">
            Join thousands of teams already automating the intelligent way.
          </p>
        </motion.div>

        {/* Testimonials grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard key={index} {...testimonial} index={index} />
          ))}
        </div>

        {/* Company logos */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          viewport={{ once: true }}
          className="flex flex-wrap items-center justify-center gap-8 md:gap-12"
        >
          <span className="text-sm text-slate-600">Trusted by teams at</span>
          {companies.map((company, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 + index * 0.1, duration: 0.3 }}
              viewport={{ once: true }}
              className="flex items-center gap-2 text-slate-500 hover:text-slate-400 transition-colors cursor-pointer"
            >
              <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center font-semibold text-sm">
                {company.initial}
              </div>
              <span className="hidden sm:inline text-sm font-medium">{company.name}</span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
